import { createContext, useContext, useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react'
import { Eye, EyeOff, LockKeyhole, Mail } from 'lucide-react'

interface AuthUser { email: string }
interface AuthContextValue { user: AuthUser; logout: () => Promise<void> }

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthGate({ children }: { children: (user: AuthUser) => ReactNode }) {
  const [status, setStatus] = useState<'loading' | 'signed-out' | 'signed-in'>('loading')
  const [user, setUser] = useState<AuthUser>()

  useEffect(() => {
    fetch('/api/auth/session', { credentials: 'same-origin', cache: 'no-store' })
      .then(async (response) => {
        if (!response.ok) {
          window.localStorage.removeItem('strata-last-user')
          setStatus('signed-out')
          return
        }
        const data = await response.json() as { user: AuthUser }
        setUser(data.user)
        window.localStorage.setItem('strata-last-user', data.user.email)
        setStatus('signed-in')
      })
      .catch(() => {
        const lastUser = window.localStorage.getItem('strata-last-user')
        if (lastUser) {
          setUser({ email: lastUser })
          setStatus('signed-in')
        } else {
          setStatus('signed-out')
        }
      })
  }, [])

  const context = useMemo<AuthContextValue | undefined>(() => user ? {
    user,
    logout: async () => {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: '{}' })
      window.localStorage.removeItem('strata-last-user')
      setUser(undefined)
      setStatus('signed-out')
    },
  } : undefined, [user])

  if (status === 'loading') return <main className="app-loading" aria-label="Comprobando tu cuenta"><span>S</span></main>
  if (status === 'signed-out' || !user || !context) return <AuthScreen onAuthenticated={(authenticated) => { setUser(authenticated); setStatus('signed-in') }} />
  return <AuthContext.Provider value={context}>{children(user)}</AuthContext.Provider>
}

export function useAuth() {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth debe utilizarse dentro de AuthGate')
  return value
}

function AuthScreen({ onAuthenticated }: { onAuthenticated: (user: AuthUser) => void }) {
  const [mode, setMode] = useState<'login' | 'register'>('register')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [visible, setVisible] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (mode === 'register' && password !== confirmPassword) {
      setError('Las contraseñas no coinciden.')
      return
    }
    setBusy(true)
    setError('')
    try {
      const response = await fetch(`/api/auth/${mode}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })
      const result = await response.json() as { user?: AuthUser; error?: string }
      if (!response.ok || !result.user) throw new Error(authError(result.error))
      window.localStorage.setItem('strata-last-user', result.user.email)
      onAuthenticated(result.user)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'No fue posible entrar. Inténtalo otra vez.')
    } finally {
      setBusy(false)
    }
  }

  const changeMode = (next: 'login' | 'register') => {
    setMode(next)
    setError('')
    setConfirmPassword('')
  }

  return (
    <main className="auth-page">
      <section className="auth-card">
        <header><span className="brand-mark">S</span><span className="eyebrow">STRATA · CUENTA PRIVADA</span><h1>{mode === 'register' ? 'Crea tu cuenta.' : 'Qué bueno verte.'}</h1><p>{mode === 'register' ? 'Tu entrenamiento, perfil y progreso quedarán guardados aunque cambies de dispositivo.' : 'Entra para recuperar tu rutina y todo tu progreso.'}</p></header>
        <div className="segmented auth-mode"><button className={mode === 'register' ? 'active' : ''} onClick={() => changeMode('register')}>Crear cuenta</button><button className={mode === 'login' ? 'active' : ''} onClick={() => changeMode('login')}>Iniciar sesión</button></div>
        <form className="auth-form" onSubmit={submit}>
          <label><Mail size={18} /><span>Correo</span><input required autoComplete="email" inputMode="email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="tu@correo.com" /></label>
          <label><LockKeyhole size={18} /><span>Contraseña</span><input required autoComplete={mode === 'register' ? 'new-password' : 'current-password'} minLength={10} maxLength={200} type={visible ? 'text' : 'password'} value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Mínimo 10 caracteres" /><button type="button" onClick={() => setVisible((value) => !value)} aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}>{visible ? <EyeOff size={18} /> : <Eye size={18} />}</button></label>
          {mode === 'register' && <label><LockKeyhole size={18} /><span>Confirmar</span><input required autoComplete="new-password" minLength={10} maxLength={200} type={visible ? 'text' : 'password'} value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} placeholder="Repite tu contraseña" /></label>}
          {error && <p className="auth-error" role="alert">{error}</p>}
          <button className="primary-button" disabled={busy}>{busy ? 'Guardando…' : mode === 'register' ? 'Crear cuenta y continuar' : 'Entrar a Strata'}</button>
        </form>
        <p className="auth-privacy">Sesión segura y persistente. Tu contraseña nunca se guarda en texto legible.</p>
      </section>
    </main>
  )
}

function authError(code?: string) {
  if (code === 'email_exists') return 'Ya existe una cuenta con ese correo. Inicia sesión.'
  if (code === 'invalid_credentials') return 'Correo o contraseña incorrectos.'
  if (code === 'invalid_email') return 'Escribe un correo válido.'
  if (code === 'weak_password') return 'La contraseña debe tener al menos 10 caracteres.'
  if (code === 'rate_limited') return 'Demasiados intentos. Espera unos minutos.'
  return 'No fue posible entrar. Inténtalo otra vez.'
}
